import { VFC } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { BaseTrigger, Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import {
  INPUT_STEP_TOKEN,
  WorkflowActionRunReportAddressLookupV1,
  WorkflowActionRunReportAdverseMediaV1,
  WorkflowActionRunReportBusinessAdverseMediaV1,
  WorkflowActionRunReportBusinessAssociatedPersonsV1,
  WorkflowActionRunReportBusinessLiensLookupV1,
  WorkflowActionRunReportBusinessLookupV1,
  WorkflowActionRunReportBusinessNonprofitV1,
  WorkflowActionRunReportBusinessRegistrationsLookupV1,
  WorkflowActionRunReportBusinessWatchlistV1,
  WorkflowActionRunReportBusinessWebsiteV1,
  WorkflowActionRunReportCryptoAddressWatchlistV1,
  WorkflowActionRunReportEmailAddressV1,
  WorkflowActionRunReportPhoneNumberV1,
  WorkflowActionRunReportProfileNonAuthoritativeV1,
  WorkflowActionRunReportProfileV1,
  WorkflowActionRunReportSocialMediaV1,
  WorkflowActionRunReportSyntheticV1,
  WorkflowActionRunReportWatchlistV1,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { ApiModel } from 'lib/interfaces/api';

type WorkflowActionRunReportV1 =
  | WorkflowActionRunReportAddressLookupV1
  | WorkflowActionRunReportAdverseMediaV1
  | WorkflowActionRunReportBusinessAdverseMediaV1
  | WorkflowActionRunReportBusinessAssociatedPersonsV1
  | WorkflowActionRunReportBusinessLiensLookupV1
  | WorkflowActionRunReportBusinessLookupV1
  | WorkflowActionRunReportBusinessNonprofitV1
  | WorkflowActionRunReportBusinessRegistrationsLookupV1
  | WorkflowActionRunReportBusinessWatchlistV1
  | WorkflowActionRunReportBusinessWebsiteV1
  | WorkflowActionRunReportCryptoAddressWatchlistV1
  | WorkflowActionRunReportEmailAddressV1
  | WorkflowActionRunReportPhoneNumberV1
  | WorkflowActionRunReportProfileV1
  | WorkflowActionRunReportProfileNonAuthoritativeV1
  | WorkflowActionRunReportSocialMediaV1
  | WorkflowActionRunReportSyntheticV1
  | WorkflowActionRunReportWatchlistV1;

interface ParamsRunReportV1Props {
  editable: boolean;
  errors: WorkflowError[];
  focusStep: (token: string) => void;
  params: WorkflowActionRunReportV1['params'];
  trigger?: Trigger;
  onMenuClose: () => void;
  updateActionParams: (newParams: WorkflowActionRunReportV1['params']) => void;
}

const ParamsRunReportV1: VFC<ParamsRunReportV1Props> = ({
  editable,
  errors,
  focusStep,
  params,
  trigger,
  onMenuClose,
  updateActionParams,
}) => {
  const isTriggerCase = trigger?.type === ApiModel.TriggerCase;
  const options = isTriggerCase
    ? [
        {
          label: 'Inquiry of Trigger Case',
          value: `${BaseTrigger}.${ApiModel.Inquiry}`,
        },
        {
          label: 'Account of Trigger Case',
          value: `${BaseTrigger}.${ApiModel.Account}`,
        },
      ]
    : [
        {
          label: 'Trigger Inquiry',
          value: BaseTrigger,
        },
        {
          label: 'Account of Trigger Inquiry',
          value: `${BaseTrigger}.${ApiModel.Account}`,
        },
      ];

  const select = (value: string) => {
    updateActionParams({
      ...params,
      object: value,
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionRunReportV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Run With' htmlFor='params-run-report__run-with-input' />
      <ReactSelect
        value={params.object}
        options={options}
        onChange={select}
        id='params-run-report__run-with'
        data-test='params-run-report__run-with-input'
        disabled={!editable}
        hasError={hasErrorFor('object')}
        onFocusOption={() => focusStep(INPUT_STEP_TOKEN)}
        onMenuClose={onMenuClose}
      />
      {renderErrorMessageFor('object')}
    </>
  );
};

export default ParamsRunReportV1;
